import { useCallback, useRef, useState } from 'react';
import { useOptionalBriefingRoomServices } from '../contexts/BriefingRoomContext';
import { useNarratorVoice } from './useNarratorVoice';
import { voiceForNpc } from './useNpcVoice';
import type { useTtsQueue } from './useTtsQueue';
import type {
  DispatchAction,
  DispatchHistoryTurn,
  DispatchRequest,
  DispatchRollOutcome,
  DispatchView,
} from '../services/types';

/**
 * V3 Voice GM (2026-06-02) — orchestrator hook.
 *
 * Wires the V2 transcript pipeline into the GMOrchestrator dispatcher
 * and the V1 TTS narrator. The caller (ScenarioRunner) supplies the
 * scenario-state callbacks; this hook handles:
 *   1. Build a DispatchRequest from the player transcript + view +
 *      voice-log history + pregen sheet.
 *   2. Call services.gmOrchestrator.dispatch.
 *   3. Push the player turn AND the GM reply to the voice log.
 *   4. Auto-narrate the GM's reply_text via services.voice.speak (if
 *      non-empty) using the persisted narrator voice.
 *   5. Apply the tool call to scenario state via the supplied
 *      callbacks (pick_decision → goToParagraph, roll_skill → roll, etc).
 *   6. Surface the dispatched action via state so a 3s UndoBanner can
 *      offer rollback.
 *
 * Atmospheric tools (describe_npc, look_around, check_inventory,
 * recall_briefing, ask_npc) don't mutate the view — only the reply_text
 * narration + voice log entry fire. V3 keeps these LIGHT; V5 wires the
 * scenario knowledge that lets the LLM speak them with real facts.
 */

export type DispatcherState = 'idle' | 'dispatching' | 'success' | 'error';

export interface GMDispatchSnapshot {
  action: DispatchAction | null;
  replyText: string;
  transcript: string;
  /** snapshot of state BEFORE the action applied (for UndoBanner rollback). */
  previousView: DispatchView;
}

export interface UseGMDispatcherCallbacks {
  /** Current view for both: building request + capturing previousView for undo. */
  currentView: DispatchView;
  /** PC sheet name (for system prompt context). */
  pregenName?: string;
  /** PC skill→value map (for the tools the LLM may roll). */
  pregenSkills?: Record<string, number>;
  /** Recent voice-log history (last 6 turns). */
  history: DispatchHistoryTurn[];
  /** V4 (2026-06-02): shared TTS queue. When set, GM rationale routes
   * through it so V4's paragraph autoplay lands AFTER the rationale.
   * Pass null/undefined for V3-only callers / tests. */
  ttsQueue?: ReturnType<typeof useTtsQueue> | null;
  /** V5 (2026-06-02): scenario id; backend looks up seed/scenarios/{id}.json
   * to build the 2nd system message (GM secret knowledge). When undefined
   * the backend default `dr_no_adventure` is used. Pass null to disable
   * scenario context entirely (V3 behavior). */
  scenarioId?: string | null;
  /** V5: paragraph numbers visited this campaign run. */
  stateHistory?: number[];
  /** V5: recent skill-roll outcomes the GM can reference for continuity. */
  stateRolls?: DispatchRollOutcome[];
  /** V5: snapshot of player inventory item ids. */
  inventory?: string[];

  /** Apply pick_decision(label) → caller looks up + navigates. Return true if dispatched. */
  onPickDecision(label: string): boolean;
  /** Apply roll_skill(skill_name, ease_factor). Return true if dispatched. */
  onRollSkill(skillName: string, easeFactor: string): boolean;
  /** A8r C4 (2026-06-04): apply force_recognize_npc(npc_id, recognized) →
   *  caller wires through to setFameOverride (typically with silent:true so
   *  the voice itself is the reveal, no extra cinematic). Return true if
   *  dispatched, false if the action was rejected (empty npc_id, etc.). */
  onFameOverride(npcId: string, recognized: boolean | null): boolean;
  /** Round 13 (2026-06-06): apply force_dice_roll(mode, notation) →
   *  caller wires through to setPendingGenericRoll so the second
   *  DiceRollTumble instance mounts with the right mode + notation.
   *  Return true if dispatched, false if the action was rejected. */
  onForceDiceRoll(spec: {
    mode: 'save' | 'damage' | 'skill_check' | 'percentile';
    notation: string;
  }): boolean;
  /** Round 15 (2026-06-09): apply force_paragraph(n) → caller wires
   *  through to goToParagraph(n) which validates n against the scenario's
   *  findParagraph + silently no-ops on out-of-bounds (mirrors the History
   *  drawer's GM force-paragraph input). Return true if dispatched, false
   *  if the action was rejected (non-integer n, n < 1, callback absent). */
  onForceParagraph?(n: number): boolean;
  /** V9.2.x (2026-06-06): apply force_chase_resolve. Caller renders
   *  ChaseSequenceCard. Optional — when omitted the dispatcher logs
   *  the action via onVoiceLogAppend('system', ...) instead. */
  onChaseResolve?(args: {
    maneuver: 'pursue' | 'flee' | 'force' | 'quick_turn' | 'double_back' | 'trick';
    bidEaseFactor: number;
    currentRange: 'close' | 'medium' | 'long' | 'distant' | 'extreme';
    rationale?: string;
  }): boolean;
  /** V9.2.x: apply force_persuasion_attempt. Caller renders
   *  PersuasionOutcomeChip. */
  onPersuasionAttempt?(args: {
    targetNpcId: string;
    requestSummary: string;
    efModifierIds?: Array<
      | 'trivial_request'
      | 'minor_request'
      | 'major_request'
      | 'deadly_request'
      | 'appropriate_emotion'
    >;
    rationale?: string;
  }): boolean;
  /** V9.2.x: apply force_seduction_stage_advance. Caller renders
   *  SeductionStageTracker. */
  onSeductionStageAdvance?(args: {
    targetNpcId: string;
    currentStage: number;
    appearanceModifierId?:
      | 'plain'
      | 'normal'
      | 'good_looking'
      | 'attractive'
      | 'striking'
      | 'sensational';
    situationalModifierIds?: Array<
      | 'candlelight'
      | 'champagne'
      | 'music'
      | 'interrupted'
      | 'hostile_environment'
    >;
    rationale?: string;
  }): boolean;
  /** V9.2.x: apply force_fame_recognition. Caller renders
   *  FameRecognitionPanel. DISTINCT from onFameOverride (which is the
   *  GM-override toggle wired to force_recognize_npc). */
  onFameRecognition?(args: {
    targetNpcId: string;
    disguiseQr?: number | null;
    rationale?: string;
  }): boolean;
  /** V9.2.x: apply force_hero_point_spend. Caller renders
   *  HeroPointSpendView. */
  onHeroPointSpend?(args: {
    currentBalance: number;
    pointsSpent: number;
    purpose:
      | 'alter_qr'
      | 'reroll'
      | 'activate_ability'
      | 'save_pc'
      | 'qr1_award_claim';
    qrTarget?: number | null;
    rationale?: string;
  }): boolean;
  /** V9.2.x: apply force_npc_encounter_roll. Caller renders
   *  NPCEncounterCard. */
  onNpcEncounterRoll?(args: {
    areaType: 'hot' | 'cold';
    modifier?: number;
    rationale?: string;
  }): boolean;
  /** V9.2.x: apply force_travel_time_compute. Caller renders
   *  TravelTimeBubble. */
  onTravelTimeCompute?(args: {
    origin: string;
    destination: string;
    mode: 'train' | 'air' | 'driving_60mph' | 'driving_100mph';
    rationale?: string;
  }): boolean;
  /** V9.2.x: apply force_xp_award. Caller renders XPAwardSummary. */
  onXpAward?(args: {
    rank: 'Rookie' | 'Agent' | '00';
    missionOutcome: 'success' | 'failure' | 'incomplete';
    rolePlayMultiplier?: number;
    rationale?: string;
  }): boolean;
  /** V9.2.x: apply force_gambling_play. Caller renders
   *  GamblingTableView. */
  onGamblingPlay?(args: {
    gameType:
      | 'chemin_de_fer'
      | 'baccarat'
      | 'blackjack'
      | 'poker'
      | 'roulette';
    betAmount: number;
    rouletteBetType?:
      | 'single_number'
      | 'red_black'
      | 'odd_even'
      | 'high_low'
      | 'column'
      | 'dozen'
      | 'split'
      | 'street'
      | 'corner'
      | 'six_line'
      | 'five_number'
      | null;
    attemptingCheat?: boolean;
    rationale?: string;
  }): boolean;
  /** V9.2.x: apply force_repair_attempt. Caller renders
   *  RepairAttemptIndicator. */
  onRepairAttempt?(args: {
    d6DamageRoll: number;
    equipmentType: string;
    hasRepairSkill?: boolean;
    rationale?: string;
  }): boolean;
  /** V9.2.x: apply force_character_creation_validate. Caller renders
   *  CharacterCreationFlowStepper. */
  onCharacterCreationValidate?(args: {
    rank: 'Rookie' | 'Agent' | '00';
    gender: 'male' | 'female';
    heightInches?: number;
    weightLbs?: number;
    appearance:
      | 'plain'
      | 'normal'
      | 'good_looking'
      | 'attractive'
      | 'striking'
      | 'sensational';
    characteristics: {
      STR: number;
      DEX: number;
      WIL: number;
      PER: number;
      INT: number;
    };
    rationale?: string;
  }): boolean;
  /** Push a player or GM voice-log entry. */
  onVoiceLogAppend(role: 'player' | 'gm' | 'system', text: string): void;
  /** Restore the view (Undo). */
  onUndoView(previousView: DispatchView): void;
}

export function useGMDispatcher(callbacks: UseGMDispatcherCallbacks) {
  const services = useOptionalBriefingRoomServices();
  const ttsQueue = callbacks.ttsQueue ?? null;
  const [narratorVoice] = useNarratorVoice();
  const [state, setState] = useState<DispatcherState>('idle');
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastSnapshot, setLastSnapshot] = useState<GMDispatchSnapshot | null>(null);
  const lastSpeakRevokeRef = useRef<(() => void) | null>(null);

  const cleanupTts = useCallback(() => {
    if (lastSpeakRevokeRef.current !== null) {
      lastSpeakRevokeRef.current();
      lastSpeakRevokeRef.current = null;
    }
  }, []);

  // V4 (2026-06-02): when a shared queue is mounted (ScenarioRunner
  // provides it), route the GM rationale through the queue so the
  // V4 paragraph autoplay LANDS AFTER it. Cinematic sequencing for free.
  // When no queue is mounted (tests / picker), fall back to the V3
  // direct-Audio path.
  const speak = useCallback(
    async (text: string, voiceOverride?: string) => {
      const trimmed = text.trim();
      if (trimmed.length === 0) {
        return;
      }
      const voice = voiceOverride ?? narratorVoice;
      const source: 'gm:rationale' | 'gm:npc-voice' =
        voiceOverride !== undefined ? 'gm:npc-voice' : 'gm:rationale';
      if (ttsQueue !== null) {
        ttsQueue.enqueue(trimmed, { voice, source });
        return;
      }
      if (services === null || !services.voice.isAvailable()) {
        return;
      }
      try {
        const result = await services.voice.speak(trimmed, { voice });
        cleanupTts();
        const audio = new Audio(result.audioUrl);
        audio.onended = () => result.revoke();
        audio.onerror = () => result.revoke();
        lastSpeakRevokeRef.current = result.revoke;
        void audio.play();
      } catch {
        // TTS is best-effort; failures are silent (V13 §11.16 — separate concerns).
      }
    },
    [services, ttsQueue, narratorVoice, cleanupTts],
  );

  const dispatchTranscript = useCallback(
    async (transcript: string) => {
      if (services === null || !services.gmOrchestrator.isAvailable()) {
        setLastError('gmOrchestrator unavailable');
        setState('error');
        return null;
      }
      const trimmed = transcript.trim();
      if (trimmed.length === 0) {
        return null;
      }
      callbacks.onVoiceLogAppend('player', trimmed);
      setState('dispatching');
      setLastError(null);

      const request: DispatchRequest = {
        transcript: trimmed,
        view: callbacks.currentView,
        history: callbacks.history,
        pregen_name: callbacks.pregenName,
        pregen_skills: callbacks.pregenSkills,
      };
      if (callbacks.scenarioId !== undefined) {
        request.scenario_id = callbacks.scenarioId === null ? undefined : callbacks.scenarioId;
      }
      if (callbacks.stateHistory !== undefined) {
        request.state_history = callbacks.stateHistory;
      }
      if (callbacks.stateRolls !== undefined) {
        request.state_rolls = callbacks.stateRolls;
      }
      if (callbacks.inventory !== undefined) {
        request.inventory = callbacks.inventory;
      }

      let response;
      try {
        response = await services.gmOrchestrator.dispatch(request);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        setLastError(message);
        callbacks.onVoiceLogAppend('system', `GM error: ${message}`);
        setState('error');
        return null;
      }

      const replyText = response.reply_text;
      if (replyText.length > 0) {
        callbacks.onVoiceLogAppend('gm', replyText);
        // V6.1.B (2026-06-02): when the action is ask_npc, the reply text IS
        // the NPC speaking, so route it through the NPC's mapped voice.
        // describe_npc keeps the narrator voice because the GM is describing
        // the NPC, not impersonating them.
        let npcVoice: string | undefined;
        if (response.action !== null && response.action.tool === 'ask_npc') {
          const npcId = response.action.args.npc_id;
          if (typeof npcId === 'string') {
            npcVoice = voiceForNpc(npcId);
          }
        }
        void speak(replyText, npcVoice);
      }

      const action = response.action;
      if (action !== null) {
        const applied = applyAction(action, callbacks);
        if (!applied) {
          // Atmospheric tools (no view mutation) or unknown tool — log it.
          callbacks.onVoiceLogAppend(
            'system',
            `GM tool: ${action.tool}(${JSON.stringify(action.args)})`,
          );
        }
      }

      const snapshot: GMDispatchSnapshot = {
        action,
        replyText,
        transcript: trimmed,
        previousView: callbacks.currentView,
      };
      setLastSnapshot(snapshot);
      setState('success');
      return snapshot;
    },
    [services, callbacks, speak],
  );

  const undo = useCallback(
    (snapshot: GMDispatchSnapshot) => {
      cleanupTts();
      callbacks.onUndoView(snapshot.previousView);
      callbacks.onVoiceLogAppend('system', `Undo: ${snapshot.transcript}`);
      setLastSnapshot(null);
      setState('idle');
    },
    [callbacks, cleanupTts],
  );

  const clearSnapshot = useCallback(() => {
    setLastSnapshot(null);
  }, []);

  return {
    state,
    lastError,
    lastSnapshot,
    dispatchTranscript,
    undo,
    clearSnapshot,
  };
}

/**
 * Round 14 (2026-06-06, Sprint 1 §4.7): infer the roll mode from a dice
 * notation string. Returns null if the notation cannot be parsed.
 *
 * Inference rules (applied in order):
 *   1. Contains d100 / d% → percentile (007 RPG convention)
 *   2. NdM where N >= 2  → damage (multi-die pool)
 *   3. 1d20 exactly       → save (canonical save die)
 *   4. Single-die non-d20 → save (1d4 / 1d6 / 1d8 / 1d12 etc)
 *   5. Otherwise          → null (reject)
 *
 * Bare 'd20' is normalized to '1d20' before applying. The accepted
 * notation grammar matches ScenarioRunner's damageValid regex:
 * `/^\d+d\d+([+-]\d+)?$/i`.
 *
 * Exported (kept module-internal) for direct vitest coverage of edge
 * cases.
 */
export function inferRollMode(notationRaw: string): {
  mode: 'save' | 'damage' | 'skill_check' | 'percentile';
  notation: string;
} | null {
  const trimmed = notationRaw.trim();
  if (trimmed.length === 0) return null;
  // Normalize bare 'd20' → '1d20'.
  const normalized = /^d\d+/i.test(trimmed) ? `1${trimmed}` : trimmed;
  if (!/^\d+d\d+([+-]\d+)?$/i.test(normalized)) {
    return null;
  }
  // d100 / d% match — disambiguation: 007 RPG convention defaults d100
  // to percentile, not skill_check. The LLM can override by specifying
  // mode='skill_check' explicitly (skipping the inference branch).
  if (/d100/i.test(normalized) || /d%/i.test(normalized)) {
    return { mode: 'percentile', notation: normalized };
  }
  const match = normalized.match(/^(\d+)d(\d+)/i)!;
  const qty = parseInt(match[1], 10);
  if (qty >= 2) {
    return { mode: 'damage', notation: normalized };
  }
  // Single-die path (qty === 1). d20 is canonical save, others also save.
  return { mode: 'save', notation: normalized };
}

/**
 * Apply a DispatchAction to scenario state via callbacks. Returns true
 * when the action mutated view-relevant state (and thus is undoable).
 * Atmospheric tools return false — they only narrate, so undo is a no-op.
 */
function applyAction(
  action: DispatchAction,
  cb: UseGMDispatcherCallbacks,
): boolean {
  switch (action.tool) {
    case 'pick_decision': {
      const label = String(action.args.label ?? '').trim();
      if (label.length === 0) {
        return false;
      }
      return cb.onPickDecision(label);
    }
    case 'roll_skill': {
      const skillName = String(action.args.skill_name ?? '').trim();
      const easeFactor = String(action.args.ease_factor ?? '5');
      if (skillName.length === 0) {
        return false;
      }
      return cb.onRollSkill(skillName, easeFactor);
    }
    case 'force_recognize_npc': {
      // A8r C4 (2026-06-04): voice-mode GM fame-recognition override.
      // Validate strictly — silently reject any malformed call rather
      // than fire setFameOverride with garbage args.
      const npcId = String(action.args.npc_id ?? '').trim();
      if (npcId.length === 0) {
        return false;
      }
      const recognizedRaw = action.args.recognized;
      if (
        recognizedRaw !== true &&
        recognizedRaw !== false &&
        recognizedRaw !== null
      ) {
        return false;
      }
      return cb.onFameOverride(npcId, recognizedRaw);
    }
    case 'force_dice_roll': {
      // Round 13 (2026-06-06): voice-mode generic dice roll. Validate
      // mode strictly + fall back to sensible per-mode default notation
      // when omitted. Reject any unknown mode silently rather than
      // firing setPendingGenericRoll with garbage args.
      //
      // Round 14 (2026-06-06, Sprint 1 §4.7): 'auto' mode added as
      // safety net. When the LLM is confident about the dice but uncertain
      // about the semantic category, it can emit mode='auto'+notation
      // and the engine infers mode from notation per these rules:
      //   d100 / d% / contains d100   → percentile (007 RPG convention)
      //   N≥2 dice pool (e.g. 2d6)   → damage
      //   1d20 exactly                → save (canonical save die)
      //   single-die non-d20          → save (1d4/1d6/1d8/1d12 etc)
      //   unparseable / empty         → reject (return false)
      // Bare 'd20' is normalized to '1d20' before applying rules.
      const modeRaw = String(action.args.mode ?? '').trim();
      const notationRaw = String(action.args.notation ?? '').trim();
      if (modeRaw === 'auto') {
        const inferred = inferRollMode(notationRaw);
        if (inferred === null) return false;
        return cb.onForceDiceRoll({ mode: inferred.mode, notation: inferred.notation });
      }
      if (
        modeRaw !== 'save' &&
        modeRaw !== 'damage' &&
        modeRaw !== 'skill_check' &&
        modeRaw !== 'percentile'
      ) {
        return false;
      }
      const mode = modeRaw as 'save' | 'damage' | 'skill_check' | 'percentile';
      const defaults: Record<typeof mode, string> = {
        save: '1d20',
        damage: '2d6',
        skill_check: '1d100',
        percentile: '1d100',
      };
      const notation = notationRaw.length > 0 ? notationRaw : defaults[mode];
      return cb.onForceDiceRoll({ mode, notation });
    }
    case 'force_chase_resolve': {
      if (cb.onChaseResolve === undefined) return false;
      const maneuver = String(action.args.maneuver ?? '').trim();
      const validManeuvers = [
        'pursue',
        'flee',
        'force',
        'quick_turn',
        'double_back',
        'trick',
      ] as const;
      if (!(validManeuvers as readonly string[]).includes(maneuver)) {
        return false;
      }
      const bidRaw = action.args.bid_ease_factor;
      const bidEaseFactor = typeof bidRaw === 'number' && Number.isInteger(bidRaw) ? bidRaw : NaN;
      if (Number.isNaN(bidEaseFactor) || bidEaseFactor < 1 || bidEaseFactor > 10) {
        return false;
      }
      const currentRange = String(action.args.current_range ?? '').trim();
      const validRanges = [
        'close',
        'medium',
        'long',
        'distant',
        'extreme',
      ] as const;
      if (!(validRanges as readonly string[]).includes(currentRange)) {
        return false;
      }
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onChaseResolve({
        maneuver: maneuver as 'pursue' | 'flee' | 'force' | 'quick_turn' | 'double_back' | 'trick',
        bidEaseFactor,
        currentRange: currentRange as 'close' | 'medium' | 'long' | 'distant' | 'extreme',
        rationale,
      });
    }
    case 'force_persuasion_attempt': {
      if (cb.onPersuasionAttempt === undefined) return false;
      const targetNpcId = String(action.args.target_npc_id ?? '').trim();
      if (targetNpcId.length === 0) return false;
      const requestSummary = String(action.args.request_summary ?? '').trim();
      if (requestSummary.length === 0) return false;
      const validModifiers = [
        'trivial_request',
        'minor_request',
        'major_request',
        'deadly_request',
        'appropriate_emotion',
      ] as const;
      const efModifierIdsRaw = action.args.ef_modifier_ids;
      let efModifierIds: ReadonlyArray<typeof validModifiers[number]> | undefined;
      if (Array.isArray(efModifierIdsRaw)) {
        const filtered = efModifierIdsRaw
          .filter((m): m is string => typeof m === 'string')
          .filter((m): m is typeof validModifiers[number] =>
            (validModifiers as readonly string[]).includes(m),
          );
        efModifierIds = filtered;
      }
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onPersuasionAttempt({
        targetNpcId,
        requestSummary,
        efModifierIds: efModifierIds === undefined ? undefined : [...efModifierIds],
        rationale,
      });
    }
    case 'force_seduction_stage_advance': {
      if (cb.onSeductionStageAdvance === undefined) return false;
      const targetNpcId = String(action.args.target_npc_id ?? '').trim();
      if (targetNpcId.length === 0) return false;
      const stageRaw = action.args.current_stage;
      const currentStage =
        typeof stageRaw === 'number' && Number.isInteger(stageRaw) ? stageRaw : NaN;
      if (Number.isNaN(currentStage) || currentStage < 0 || currentStage > 5) {
        return false;
      }
      const validAppearances = [
        'plain',
        'normal',
        'good_looking',
        'attractive',
        'striking',
        'sensational',
      ] as const;
      const appearanceRaw = String(action.args.appearance_modifier_id ?? '').trim();
      const appearanceModifierId =
        appearanceRaw.length > 0 && (validAppearances as readonly string[]).includes(appearanceRaw)
          ? (appearanceRaw as typeof validAppearances[number])
          : undefined;
      const validSituational = [
        'candlelight',
        'champagne',
        'music',
        'interrupted',
        'hostile_environment',
      ] as const;
      const situationalRaw = action.args.situational_modifier_ids;
      let situationalModifierIds: Array<typeof validSituational[number]> | undefined;
      if (Array.isArray(situationalRaw)) {
        situationalModifierIds = situationalRaw
          .filter((m): m is string => typeof m === 'string')
          .filter((m): m is typeof validSituational[number] =>
            (validSituational as readonly string[]).includes(m),
          );
      }
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onSeductionStageAdvance({
        targetNpcId,
        currentStage,
        appearanceModifierId,
        situationalModifierIds,
        rationale,
      });
    }
    case 'force_fame_recognition': {
      if (cb.onFameRecognition === undefined) return false;
      const targetNpcId = String(action.args.target_npc_id ?? '').trim();
      if (targetNpcId.length === 0) return false;
      const disguiseRaw = action.args.disguise_qr;
      let disguiseQr: number | null | undefined;
      if (disguiseRaw === null) {
        disguiseQr = null;
      } else if (typeof disguiseRaw === 'number' && Number.isInteger(disguiseRaw)) {
        disguiseQr = disguiseRaw;
      } else {
        disguiseQr = undefined;
      }
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onFameRecognition({ targetNpcId, disguiseQr, rationale });
    }
    case 'force_hero_point_spend': {
      if (cb.onHeroPointSpend === undefined) return false;
      const balanceRaw = action.args.current_balance;
      const currentBalance =
        typeof balanceRaw === 'number' && Number.isInteger(balanceRaw) ? balanceRaw : NaN;
      if (Number.isNaN(currentBalance) || currentBalance < 0) return false;
      const spentRaw = action.args.points_spent;
      const pointsSpent =
        typeof spentRaw === 'number' && Number.isInteger(spentRaw) ? spentRaw : NaN;
      if (Number.isNaN(pointsSpent) || pointsSpent < 1) return false;
      const validPurposes = [
        'alter_qr',
        'reroll',
        'activate_ability',
        'save_pc',
        'qr1_award_claim',
      ] as const;
      const purpose = String(action.args.purpose ?? '').trim();
      if (!(validPurposes as readonly string[]).includes(purpose)) return false;
      const qrTargetRaw = action.args.qr_target;
      let qrTarget: number | null | undefined;
      if (qrTargetRaw === null) qrTarget = null;
      else if (typeof qrTargetRaw === 'number' && Number.isInteger(qrTargetRaw)) {
        qrTarget = qrTargetRaw;
      } else qrTarget = undefined;
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onHeroPointSpend({
        currentBalance,
        pointsSpent,
        purpose: purpose as typeof validPurposes[number],
        qrTarget,
        rationale,
      });
    }
    case 'force_npc_encounter_roll': {
      if (cb.onNpcEncounterRoll === undefined) return false;
      const areaType = String(action.args.area_type ?? '').trim();
      if (areaType !== 'hot' && areaType !== 'cold') return false;
      const modifierRaw = action.args.modifier;
      const modifier =
        typeof modifierRaw === 'number' && Number.isInteger(modifierRaw) ? modifierRaw : undefined;
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onNpcEncounterRoll({ areaType, modifier, rationale });
    }
    case 'force_travel_time_compute': {
      if (cb.onTravelTimeCompute === undefined) return false;
      const origin = String(action.args.origin ?? '').trim();
      if (origin.length === 0) return false;
      const destination = String(action.args.destination ?? '').trim();
      if (destination.length === 0) return false;
      const validModes = [
        'train',
        'air',
        'driving_60mph',
        'driving_100mph',
      ] as const;
      const mode = String(action.args.mode ?? '').trim();
      if (!(validModes as readonly string[]).includes(mode)) return false;
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onTravelTimeCompute({
        origin,
        destination,
        mode: mode as typeof validModes[number],
        rationale,
      });
    }
    case 'force_xp_award': {
      if (cb.onXpAward === undefined) return false;
      const validRanks = ['Rookie', 'Agent', '00'] as const;
      const rank = String(action.args.rank ?? '').trim();
      if (!(validRanks as readonly string[]).includes(rank)) return false;
      const validOutcomes = ['success', 'failure', 'incomplete'] as const;
      const missionOutcome = String(action.args.mission_outcome ?? '').trim();
      if (!(validOutcomes as readonly string[]).includes(missionOutcome)) return false;
      const rpRaw = action.args.role_play_multiplier;
      let rolePlayMultiplier: number | undefined;
      if (typeof rpRaw === 'number' && rpRaw >= 0.5 && rpRaw <= 1.5) {
        rolePlayMultiplier = rpRaw;
      }
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onXpAward({
        rank: rank as typeof validRanks[number],
        missionOutcome: missionOutcome as typeof validOutcomes[number],
        rolePlayMultiplier,
        rationale,
      });
    }
    case 'force_gambling_play': {
      if (cb.onGamblingPlay === undefined) return false;
      const validGames = [
        'chemin_de_fer',
        'baccarat',
        'blackjack',
        'poker',
        'roulette',
      ] as const;
      const gameType = String(action.args.game_type ?? '').trim();
      if (!(validGames as readonly string[]).includes(gameType)) return false;
      const betRaw = action.args.bet_amount;
      const betAmount = typeof betRaw === 'number' && Number.isInteger(betRaw) ? betRaw : NaN;
      if (Number.isNaN(betAmount) || betAmount < 1) return false;
      const validBetTypes = [
        'single_number',
        'red_black',
        'odd_even',
        'high_low',
        'column',
        'dozen',
        'split',
        'street',
        'corner',
        'six_line',
        'five_number',
      ] as const;
      const betTypeRaw = action.args.roulette_bet_type;
      let rouletteBetType:
        | typeof validBetTypes[number]
        | null
        | undefined;
      if (betTypeRaw === null) rouletteBetType = null;
      else if (
        typeof betTypeRaw === 'string' &&
        (validBetTypes as readonly string[]).includes(betTypeRaw)
      ) {
        rouletteBetType = betTypeRaw as typeof validBetTypes[number];
      } else rouletteBetType = undefined;
      const cheatRaw = action.args.attempting_cheat;
      const attemptingCheat = typeof cheatRaw === 'boolean' ? cheatRaw : undefined;
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onGamblingPlay({
        gameType: gameType as typeof validGames[number],
        betAmount,
        rouletteBetType,
        attemptingCheat,
        rationale,
      });
    }
    case 'force_repair_attempt': {
      if (cb.onRepairAttempt === undefined) return false;
      const rollRaw = action.args.d6_damage_roll;
      const d6DamageRoll = typeof rollRaw === 'number' && Number.isInteger(rollRaw) ? rollRaw : NaN;
      if (Number.isNaN(d6DamageRoll) || d6DamageRoll < 1 || d6DamageRoll > 6) return false;
      const equipmentType = String(action.args.equipment_type ?? '').trim();
      if (equipmentType.length === 0) return false;
      const skillRaw = action.args.has_repair_skill;
      const hasRepairSkill = typeof skillRaw === 'boolean' ? skillRaw : undefined;
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onRepairAttempt({ d6DamageRoll, equipmentType, hasRepairSkill, rationale });
    }
    case 'force_character_creation_validate': {
      if (cb.onCharacterCreationValidate === undefined) return false;
      const validRanks = ['Rookie', 'Agent', '00'] as const;
      const rank = String(action.args.rank ?? '').trim();
      if (!(validRanks as readonly string[]).includes(rank)) return false;
      const gender = String(action.args.gender ?? '').trim();
      if (gender !== 'male' && gender !== 'female') return false;
      const heightRaw = action.args.height_inches;
      const heightInches =
        typeof heightRaw === 'number' && Number.isInteger(heightRaw) ? heightRaw : undefined;
      const weightRaw = action.args.weight_lbs;
      const weightLbs =
        typeof weightRaw === 'number' && Number.isInteger(weightRaw) ? weightRaw : undefined;
      const validAppearances = [
        'plain',
        'normal',
        'good_looking',
        'attractive',
        'striking',
        'sensational',
      ] as const;
      const appearance = String(action.args.appearance ?? '').trim();
      if (!(validAppearances as readonly string[]).includes(appearance)) return false;
      const charsRaw = action.args.characteristics;
      if (charsRaw === null || typeof charsRaw !== 'object') return false;
      const cc = charsRaw as Record<string, unknown>;
      const requiredKeys = ['STR', 'DEX', 'WIL', 'PER', 'INT'] as const;
      const characteristics: { STR: number; DEX: number; WIL: number; PER: number; INT: number } = {
        STR: 0, DEX: 0, WIL: 0, PER: 0, INT: 0,
      };
      for (const key of requiredKeys) {
        const v = cc[key];
        if (typeof v !== 'number' || !Number.isInteger(v) || v < 1 || v > 15) {
          return false;
        }
        characteristics[key] = v;
      }
      const rationaleRaw = action.args.rationale;
      const rationale = typeof rationaleRaw === 'string' ? rationaleRaw : undefined;
      return cb.onCharacterCreationValidate({
        rank: rank as typeof validRanks[number],
        gender,
        heightInches,
        weightLbs,
        appearance: appearance as typeof validAppearances[number],
        characteristics,
        rationale,
      });
    }
    case 'force_paragraph': {
      // Round 15 (2026-06-09): voice-mode paragraph navigation. Validate
      // n strictly — positive integer only. Out-of-bounds-relative-to-
      // scenario is enforced downstream by goToParagraph's findParagraph
      // silent no-op (mirrors the History drawer's GM force-paragraph
      // input behavior). Closes the gap the 2026-06-09 voice-verification
      // session caught: "Jump to paragraph 29." was falling back to
      // pick_decision({label:"29"}) → onPickDecision rejected → silent
      // no-op via atmospheric fallback log.
      if (cb.onForceParagraph === undefined) return false;
      const nRaw = action.args.n;
      if (typeof nRaw !== 'number' || !Number.isInteger(nRaw) || nRaw < 1) {
        return false;
      }
      return cb.onForceParagraph(nRaw);
    }
    case 'describe_npc':
    case 'look_around':
    case 'check_inventory':
    case 'recall_briefing':
    case 'ask_npc':
      return false;
    default:
      return false;
  }
}
